import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { fetchById as fetchExpFabById } from '../../../features/empresa-estrangeira/empresaEstrangeiraAPI';
import { selectPaises } from '../../../features/pais/paisSlice';
import { getLocalidadeOrDefault } from '../../../utils/general/general-utils';

const useFabricantesBond = (values, catalogoProdutoId, importadorId) => {
  const { t } = useTranslation();

  const form = useForm();

  const paises = useSelector(selectPaises);

  const formProps = useMemo(
    () => ({
      rootName: 'ignorableFields.fabricanteBond',
      fields: [
        {
          name: 'fabricanteId',
          label: 'Fabricante',
          required: false,
        },
        {
          name: 'paisId',
          label: 'País',
          required: false,
        },
        {
          name: 'isConhecido',
          label: 'Conhecido?',
          required: false,
        },
      ],
    }),
    []
  );

  const showPaisField = _.get(values, `${formProps.rootName}.isConhecido`) === 'NÃO';
  const showFabricanteField = _.get(values, `${formProps.rootName}.isConhecido`) === 'SIM';

  const listProps = useMemo(
    () => ({
      name: 'relacaoFabricantes',
      columns: [
        {
          field: 'pessoaJuridica.id',
          headerName: t('com.muralis.qcx.codigo'),
          headerAlign: 'center',
          align: 'center',
          type: 'string',
          flex: 150,
          valueGetter: ({ row }) => row?.fabricante?.id,
        },
        {
          field: 'pessoaJuridica.nomeResumido',
          headerName: t('com.muralis.qcx.cliente.label'),
          type: 'string',
          flex: 300,
          valueGetter: ({ row }) =>
            row?.fabricante?.pessoaJuridica?.nomeResumido ?? row?.fabricante?.pessoaJuridica?.nome,
        },
        {
          field: 'isConhecido',
          headerName: 'Conhecido ?',
          type: 'string',
          flex: 150,
          valueGetter: ({ row }) => (row?.isConhecido ? 'SIM' : 'NÃO'),
        },
        {
          field: 'pais.description',
          headerName: t('com.muralis.qcx.endereco.pais'),
          type: 'string',
          flex: 200,
          valueGetter: ({ row }) => row?.pais?.description ?? '-',
        },
        {
          field: 'endereco.pais.description',
          headerName: t('com.muralis.qcx.endereco.endereco'),
          type: 'string',
          flex: 300,
          valueGetter: ({ row }) => getLocalidadeOrDefault(row?.fabricante?.endereco, '-'),
        },
        {
          field: 'codigoExterno',
          headerName: 'Código OPE',
          type: 'string',
          flex: 150,
          valueGetter: ({ row }) => {
            let codOpe = '';
            if (importadorId) {
              codOpe = row?.fabricante?.relacaoClientes?.find(
                (r) => r?.cliente?.id === importadorId
              )?.codigoExportadorSiscomex;
            } else {
              codOpe = row?.fabricante?.relacaoClientes?.[0]?.codigoExportadorSiscomex;
            }
            return codOpe;
          },
        },
      ],
    }),
    []
  );

  const idToExportadorFabricanteEntity = async (id) => {
    if (!id) return undefined;
    try {
      const response = await fetchExpFabById(id);
      return response?.data;
    } catch (err) {
      return undefined;
    }
  };
  const idToPaisEntity = (id) => {
    if (!id) return undefined;
    return paises.find((p) => p.id === id);
  };
  const isVinculoSame = (existingVinculo, currentVinculo) => {
    const opeConhecidoIgual = existingVinculo?.fabricante?.id === currentVinculo?.fabricanteId;
    const paisDesconhecidoIgual =
      currentVinculo?.isConhecido === 'NÃO' && existingVinculo?.pais?.id === currentVinculo?.paisId;
    return opeConhecidoIgual || paisDesconhecidoIgual;
  };
  const handleAdd = useCallback(
    async (currentVinculo, currentVinculos, handleSuccessAddVinculo) => {
      if (currentVinculos === null) {
        currentVinculos = _.get(form.getState().values, listProps.name);
      }

      const fab = await idToExportadorFabricanteEntity(currentVinculo?.fabricanteId);

      let pais = idToPaisEntity(currentVinculo?.paisId);
      if (!pais) {
        pais = fab?.endereco?.pais ?? fab?.endereco?.estado?.pais ?? fab?.endereco?.cidade?.estado?.pais;
      }

      const novoVinculo = {
        id: uuid(),
        ...currentVinculo,
        fabricante: fab || null,
        pais: pais || null,
        isConhecido: currentVinculo?.isConhecido === 'SIM',
        catalogoProduto: { id: catalogoProdutoId },
      };

      const updatedVinculos = [...currentVinculos, novoVinculo];
      if (handleSuccessAddVinculo) {
        handleSuccessAddVinculo(updatedVinculos);
      } else {
        form.change(listProps.name, updatedVinculos);
      }
    },
    [form, listProps, formProps, catalogoProdutoId]
  );
  const handleUpdate = useCallback(
    async (currentVinculo, currentVinculos, handleSuccessUpdateVinculo) => {
      const fab = await idToExportadorFabricanteEntity(currentVinculo?.fabricanteId);
      const pais = idToPaisEntity(currentVinculo?.paisId);

      const updatedVinculo = {
        id: uuid(),
        ...currentVinculo,
        fabricante: fab || null,
        pais: pais || null,
        isConhecido: currentVinculo?.isConhecido === 'SIM',
        catalogoProduto: { id: catalogoProdutoId },
      };
      const updatedVinculos =
        currentVinculos?.map((existingVinculo) =>
          isVinculoSame(existingVinculo, currentVinculo) ? updatedVinculo : existingVinculo
        ) || currentVinculos;
      handleSuccessUpdateVinculo(updatedVinculos);
    },
    [catalogoProdutoId]
  );
  const handleAlreadyExists = useCallback(
    (currentVinculo, currentVinculosList) =>
      currentVinculosList.some((vinculo) => isVinculoSame(vinculo, currentVinculo)),
    []
  );

  return {
    formProps,
    listProps,
    handleAdd,
    handleUpdate,
    handleAlreadyExists,
    showPaisField,
    showFabricanteField,
  };
};

export default useFabricantesBond;
